/**
 * [AI補完仕様] このコメントはAI補完（コード自動生成）に構造情報として渡されます。変更時は実装品質に直結するため正確に保つこと。
 *
 * APIクライアント（axios）— 認証有効時はAuthorizationヘッダー(Bearer)を自動付与する。アクセストークン期限切れ(401)時はRefresh Tokenで自動更新し、元のリクエストをリトライする。
 *
 * @contract 各メソッドはPromise<AxiosResponse>を返す。値の取得にはレスポンスの.dataを使うこと。例: const res = await apiClient.get('/items'); return res.data.data ?? res.data;
 * @contract URLに/apiプレフィックスを含めないこと（自動付与される）。例: get('/dashboard/stats') ← 正しい、get('/api/dashboard/stats') ← 誤り。
 * @contract ファイルダウンロード時はget(url, { responseType: 'blob' })を使うこと（認証ヘッダーが必要なため）。
 * @contract 401発生時は本ファイルがtoken-store側のrefreshAccessToken()を呼んで自動リフレッシュ・リトライする（同時に複数の401が発生してもリフレッシュは1回にまとめられる。利用側は意識不要）。リフレッシュも失敗した場合のみonUnauthorized（ログアウト）を呼ぶ。
 * @signature useApiClient(): { get(url, config?): Promise<AxiosResponse>, post(url, body?, config?): Promise<AxiosResponse>, put(url, body?, config?): Promise<AxiosResponse>, delete(url, config?): Promise<AxiosResponse> }
 */
import axios from 'axios'

import { useAuth } from './auth/auth-context'
import { getAccessToken, refreshAccessToken } from './auth/token-store'

import { useMemo } from 'react'

import { getActiveTraceHeaders, notifyFetchComplete } from './debug/otel-web'


const API_BASE = '/api'


const SINV_DEBUG_API = (import.meta as any).env?.VITE_SINV_DEBUG_API || 'http://localhost:8000/api/__debug/error';

function reportApiError(url: string, method: string, status: number, body: any) {
  fetch(SINV_DEBUG_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: 'apiError',
      origin: 'frontend',
      level: 'error',
      message: `${method} ${url} → ${status}`,
      status,
      url,
      method,
      responseBody: typeof body === 'string' ? body.slice(0, 500) : JSON.stringify(body).slice(0, 500),
      timestamp: new Date().toISOString(),
    }),
  }).catch(() => {});
}



function createAxiosInstance(onUnauthorized: () => void) {
  const instance = axios.create({ baseURL: API_BASE })

  instance.interceptors.request.use(config => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const traceHeaders = getActiveTraceHeaders()
    for (const [k, v] of Object.entries(traceHeaders)) {
      config.headers[k] = v
    }
    const testId = (window as any).__SINV_E2E_TEST_ID;
    if (testId) config.headers['x-sinv-test-id'] = testId;

    return config
  })

  instance.interceptors.response.use(
    res => {

      notifyFetchComplete()

      return res
    },
    async error => {

      notifyFetchComplete()
      if (error.response?.status >= 400) {
        reportApiError(
          error.config?.url ?? '',
          error.config?.method?.toUpperCase() ?? 'GET',
          error.response?.status,
          error.response?.data,
        )
      }

      const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          const newToken = await refreshAccessToken()
          originalRequest.headers = originalRequest.headers ?? {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return instance(originalRequest)
        } catch {
          onUnauthorized()
          throw new Error('認証が無効です。再ログインしてください。')
        }
      }
      return Promise.reject(error)
    }
  )

  return instance
}

export function useApiClient() {
  const { logout } = useAuth()

  return useMemo(() => {
    const instance = createAxiosInstance(logout)
    return {
      get:    (url: string, config?: object) => instance.get(url, config),
      post:   (url: string, body?: any, config?: object) => instance.post(url, body, config),
      put:    (url: string, body?: any, config?: object) => instance.put(url, body, config),
      delete: (url: string, config?: object) => instance.delete(url, config),
    }
  }, [logout])
}
